/**
 * MetaInfoDatabaseCard — one collapsible database row inside a connection
 * group, hosting the loose-FK + JSON-column listings via MetaInfoSections.
 */

import { ChevronDown, ChevronRight } from 'lucide-react';
import { MetaInfoSections } from '../../../../workspace/common/components/meta-info/MetaInfoSections';
import { useConnectionStore } from '../../../../connections/store/connectionStore';
import { isValidEngineId } from '../../../../../core/engines';
import type {
  DatabaseMetaInfo,
  EnumColumnDeclaration,
  JsonColumnDeclaration,
  LooseRelationship,
  SpatialColumnDeclaration,
} from '@/types/electron';

export type MetaInfoDatabaseCardProps = {
  connectionId: string;
  metaInfo: DatabaseMetaInfo;
  open: boolean;
  onToggle: () => void;
  onRemoveLooseRel: (rel: LooseRelationship, label: string) => void;
  onRemoveJsonCol: (decl: JsonColumnDeclaration) => void;
  onRemoveEnumCol: (decl: EnumColumnDeclaration) => void;
  onRemoveSpatialCol: (decl: SpatialColumnDeclaration) => void;
};

export function MetaInfoDatabaseCard({
  connectionId,
  metaInfo,
  open,
  onToggle,
  onRemoveLooseRel,
  onRemoveJsonCol,
  onRemoveEnumCol,
  onRemoveSpatialCol,
}: MetaInfoDatabaseCardProps) {
  const looseCount = metaInfo.looseRelationships.length;
  const jsonCount = metaInfo.jsonColumns.length;
  const enumCount = metaInfo.enumColumns.length;
  const spatialCount = metaInfo.spatialColumns.length;
  const engineRaw = useConnectionStore((s) =>
    s.connections.find((c) => c.id === connectionId)?.engine,
  );
  const engineForConnection = engineRaw && isValidEngineId(engineRaw) ? engineRaw : undefined;
  return (
    <div
      className="border rounded-md bg-muted/20"
      data-testid={`metainfo-settings-db-${connectionId}-${metaInfo.database}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium hover:bg-muted/40 rounded-md"
        aria-expanded={open}
        data-testid={`metainfo-settings-db-toggle-${connectionId}-${metaInfo.database}`}
      >
        {open ? (
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
        )}
        <span className="truncate font-mono" translate="no">
          {metaInfo.database}
        </span>
        <span className="text-[11px] text-muted-foreground ml-auto">
          {looseCount} loose · {jsonCount} JSON · {enumCount} enum · {spatialCount} coord
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3">
          <MetaInfoSections
            looseRelationships={metaInfo.looseRelationships}
            jsonColumns={metaInfo.jsonColumns}
            jsonStructureCache={metaInfo.jsonStructureCache}
            enumColumns={metaInfo.enumColumns}
            enumValueCache={metaInfo.enumValueCache}
            referenceTables={metaInfo.referenceTables}
            tableDisplayColumns={metaInfo.tableDisplayColumns}
            spatialColumns={metaInfo.spatialColumns}
            onRemoveLooseRel={onRemoveLooseRel}
            onRemoveJsonCol={onRemoveJsonCol}
            onRemoveEnumCol={onRemoveEnumCol}
            onRemoveSpatialCol={onRemoveSpatialCol}
            testIdPrefix={`metainfo-settings-${connectionId}-${metaInfo.database}`}
            connectionId={connectionId}
            database={metaInfo.database}
            engine={engineForConnection}
            detailsSourceLabel="settings_details"
          />
        </div>
      )}
    </div>
  );
}
